import { Link } from 'react-router-dom';
import { Mic, MicOff, Phone, PhoneOff } from 'lucide-react';
import { CALL_STATUS, GUEST_UI_STATE, type CallStatus, type GuestUiState } from '@hotel-voice/shared';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { StateView } from '@/components/common/StateView';
import { Button } from '@/components/ui/button';
import { useReadyGuest } from '@/contexts/GuestSessionContext';
import { useGuestCall } from '@/hooks/useGuestCall';
import { cn } from '@/lib/utils';
import { AlertTriangle, PhoneCall } from 'lucide-react';

/**
 * Call Reception. The call itself — `useGuestCall`, the Agora service, the
 * Firestore call watcher — is untouched from before; this page only draws
 * the state machine it exposes (IDLE → CALLING → RINGING → CONNECTED → ENDED)
 * with the app's components. The tab bar is hidden while a call is live so a
 * stray tap cannot navigate away from it.
 */
export function CallPage() {
  const { session, location } = useReadyGuest();
  const call = useGuestCall(location);

  const live =
    call.uiState === GUEST_UI_STATE.CALLING ||
    call.uiState === GUEST_UI_STATE.RINGING ||
    call.uiState === GUEST_UI_STATE.CONNECTED ||
    call.uiState === GUEST_UI_STATE.RECONNECTING;

  if (live) {
    return (
      <AppShell showNav={false}>
        <InCall
          uiState={call.uiState}
          callStatus={call.callStatus}
          roomNumber={session.roomNumber}
          elapsedSeconds={call.elapsedSeconds}
          muted={call.muted}
          errorMessage={call.errorMessage}
          onHangUp={() => void call.hangUp()}
          onToggleMute={() => void call.toggleMute()}
        />
      </AppShell>
    );
  }

  if (call.uiState === GUEST_UI_STATE.ENDED) {
    return (
      <AppShell>
        <PageHeader title="Call Reception" />
        <StateView icon={PhoneCall} title={endedTitle(call.callStatus)} message={endedMessage(call.callStatus)}>
          <Button onClick={call.reset}>
            <Phone /> Call again
          </Button>
          <Button asChild variant="ghost">
            <Link to="/">Back to home</Link>
          </Button>
        </StateView>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <PageHeader title="Call Reception" subtitle="Available 24/7" />
      <div className="flex flex-1 flex-col items-center justify-center gap-6 px-6 py-10 text-center">
        <div className="bg-tile-green flex size-24 items-center justify-center rounded-full">
          <Phone className="size-10 text-emerald-600" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Need assistance?</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Talk to reception from Room {session.roomNumber}. The call runs over the internet — no
            phone charges.
          </p>
        </div>
        <Button size="lg" className="w-full max-w-xs bg-emerald-600 hover:bg-emerald-600/90" onClick={() => void call.startCall()}>
          <Phone /> Call Reception
        </Button>
        {call.errorMessage && (
          <div className="bg-destructive/10 text-destructive flex w-full max-w-xs items-start gap-2 rounded-xl px-3 py-2 text-left text-sm" role="alert">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <span className="flex-1">{call.errorMessage}</span>
            <Button variant="ghost" size="sm" onClick={call.reset}>
              Dismiss
            </Button>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function formatDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function statusLine(uiState: GuestUiState, callStatus: CallStatus | null): string {
  if (uiState === GUEST_UI_STATE.RECONNECTING) return 'Reconnecting…';
  if (uiState === GUEST_UI_STATE.CONNECTED) {
    return callStatus === CALL_STATUS.CONNECTED ? 'Connected' : 'Connecting…';
  }
  return 'Calling Reception…';
}

/**
 * A declined call and a missed call get the same reassuring framing — the
 * guest does not need to know that someone chose not to pick up.
 */
function endedTitle(callStatus: CallStatus | null): string {
  switch (callStatus) {
    case CALL_STATUS.MISSED:
    case CALL_STATUS.DECLINED:
      return 'Reception is busy';
    case CALL_STATUS.CANCELLED:
      return 'Call cancelled';
    default:
      return 'Call ended';
  }
}

function endedMessage(callStatus: CallStatus | null): string | undefined {
  switch (callStatus) {
    case CALL_STATUS.MISSED:
    case CALL_STATUS.DECLINED:
      return "Reception couldn't take your call. Please try again in a moment.";
    default:
      return undefined;
  }
}

function InCall({
  uiState,
  callStatus,
  roomNumber,
  elapsedSeconds,
  muted,
  errorMessage,
  onHangUp,
  onToggleMute,
}: {
  uiState: GuestUiState;
  callStatus: CallStatus | null;
  roomNumber: string;
  elapsedSeconds: number;
  muted: boolean;
  errorMessage: string | null;
  onHangUp: () => void;
  onToggleMute: () => void;
}) {
  const connected = uiState === GUEST_UI_STATE.CONNECTED && callStatus === CALL_STATUS.CONNECTED;
  const ringing = uiState === GUEST_UI_STATE.CALLING || uiState === GUEST_UI_STATE.RINGING;

  return (
    <div className="flex flex-1 flex-col items-center justify-between bg-[radial-gradient(120%_120%_at_50%_0%,oklch(0.3_0.1_262)_0%,oklch(0.18_0.04_262)_100%)] px-6 py-16 text-center text-white">
      <div>
        <p className="text-sm text-white/70" role="status">
          {statusLine(uiState, callStatus)}
        </p>
        <h2 className="mt-2 text-3xl font-bold">Reception</h2>
        <p className="mt-1 text-white/70">Room {roomNumber}</p>
        {connected && (
          <p className="mt-4 text-2xl font-semibold tabular-nums" aria-label="Call duration">
            {formatDuration(elapsedSeconds)}
          </p>
        )}
        {errorMessage && (
          <p className="mt-4 rounded-xl bg-white/10 px-3 py-2 text-sm text-red-200" role="alert">
            {errorMessage}
          </p>
        )}
      </div>

      <div className="relative flex size-40 items-center justify-center">
        {ringing && <span className="animate-ring-pulse absolute inset-0 rounded-full bg-white/20" aria-hidden="true" />}
        <span className="flex size-28 items-center justify-center rounded-full bg-white/15">
          <Phone className="size-12" aria-hidden="true" />
        </span>
      </div>

      <div className="flex items-center gap-6">
        {!ringing && (
          <Button
            variant="secondary"
            size="icon"
            className={cn('size-16 rounded-full', muted && 'bg-white text-black')}
            onClick={onToggleMute}
            aria-pressed={muted}
            aria-label={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <MicOff className="size-6" /> : <Mic className="size-6" />}
          </Button>
        )}
        <Button
          variant="destructive"
          size="icon"
          className="size-16 rounded-full"
          onClick={onHangUp}
          aria-label={ringing ? 'Cancel call' : 'End call'}
        >
          <PhoneOff className="size-6" />
        </Button>
      </div>
    </div>
  );
}
